import React, { useMemo } from "react";
import { Box, Card, Text, Stack, Flex, useMantineTheme } from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { GazeData } from "types/testTypes";
import { useTranslation } from "react-i18next";

interface GazeDisplacementViewerProps {
  data: GazeData[];
  width?: number | string;
  height?: number | string;
}

const GazeDisplacementViewer: React.FC<GazeDisplacementViewerProps> = ({ data }) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const LEFT_COLOR = theme.colors.blue[6];
  const RIGHT_COLOR = theme.colors.green[6];

  const { horizontalData, verticalData } = useMemo(() => {
    if (!data || data.length === 0) return { horizontalData: [], verticalData: [] };

    // The headset records the sample time at the top level as `Timestamp`, in
    // deciseconds (elapsed seconds × 10), while the pupil chart plots `timeStamp`
    // directly in seconds. Divide by 10 so both charts share the same time axis.
    const processedData = data.map((point, index) => {
      const raw = point.Timestamp != null ? point.Timestamp / 10 : index;

      const timeSeconds = Math.round(raw * 10) / 10;

      return {
        time: timeSeconds,
        leftX:
          point.leftGazePosition?.x !== undefined
            ? Number(point.leftGazePosition.x.toFixed(2))
            : null,
        rightX:
          point.rightGazePosition?.x !== undefined
            ? Number(point.rightGazePosition.x.toFixed(2))
            : null,
        leftY:
          point.leftGazePosition?.y !== undefined
            ? Number(point.leftGazePosition.y.toFixed(2))
            : null,
        rightY:
          point.rightGazePosition?.y !== undefined
            ? Number(point.rightGazePosition.y.toFixed(2))
            : null,
      };
    });

    return {
      horizontalData: processedData,
      verticalData: processedData,
    };
  }, [data]);

  return (
    <Stack gap="lg">
      <Card withBorder radius="md" shadow="xs" p="md">
        <Text size="lg" fw={600} mb="md">
          {t("horizontalDisplacement")}
        </Text>
        <Box h={250}>
          {horizontalData.length > 0 ? (
            <LineChart
              h={250}
              data={horizontalData}
              dataKey="time"
              series={[
                { name: "leftX", label: t("leftEye"), color: LEFT_COLOR },
                { name: "rightX", label: t("rightEye"), color: RIGHT_COLOR },
              ]}
              curveType="monotone"
              withDots={false}
              connectNulls={true}
              gridAxis="xy"
              withLegend
              legendProps={{ verticalAlign: "top" }}
              xAxisLabel={t("extraTests.cover.timeSeconds")}
              yAxisLabel={t("gaze.horizontalPosition")}
              valueFormatter={(value) =>
                value !== null ? Number(value).toFixed(2) : t("common.noDataAvailable")
              }
              referenceLines={[{ y: 0, color: "gray.5", label: t("gaze.center") }]}
            />
          ) : (
            <Flex justify="center" align="center" h="100%">
              <Text c="dimmed">{t("gaze.noHorizontalDataAvailable")}</Text>
            </Flex>
          )}
        </Box>
      </Card>

      <Card withBorder radius="md" shadow="xs" p="md">
        <Text size="lg" fw={600} mb="md">
          {t("verticalDisplacement")}
        </Text>
        <Box h={250}>
          {verticalData.length > 0 ? (
            <LineChart
              h={250}
              data={verticalData}
              dataKey="time"
              series={[
                { name: "leftY", label: t("leftEye"), color: LEFT_COLOR },
                { name: "rightY", label: t("rightEye"), color: RIGHT_COLOR },
              ]}
              curveType="monotone"
              withDots={false}
              connectNulls={true}
              gridAxis="xy"
              withLegend
              legendProps={{ verticalAlign: "top" }}
              xAxisLabel={t("extraTests.cover.timeSeconds")}
              yAxisLabel={t("gaze.verticalPosition")}
              valueFormatter={(value) =>
                value !== null ? Number(value).toFixed(2) : t("common.noDataAvailable")
              }
              referenceLines={[{ y: 0, color: "gray.5", label: t("gaze.center") }]}
            />
          ) : (
            <Flex justify="center" align="center" h="100%">
              <Text c="dimmed">{t("gaze.noVerticalDataAvailable")}</Text>
            </Flex>
          )}
        </Box>
      </Card>
      <Text size="xs" c="dimmed" ta="center">
        {data?.length > 0
          ? t("gaze.displayingDataPoints", { count: data?.length })
          : t("gaze.noDataAvailable")}
      </Text>
    </Stack>
  );
};

export default GazeDisplacementViewer;
